import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "column-chooser:";

export type ColumnOverrides = Record<string, boolean>;

const readOverrides = (storageKey: string): ColumnOverrides => {
	try {
		const raw = globalThis.sessionStorage?.getItem(
			`${STORAGE_PREFIX}${storageKey}`,
		);
		if (!raw) return {};
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return {};
		const result: ColumnOverrides = {};
		for (const [key, value] of Object.entries(
			parsed as Record<string, unknown>,
		)) {
			if (typeof value === "boolean") {
				result[key] = value;
			}
		}
		return result;
	} catch {
		return {};
	}
};

const writeOverrides = (storageKey: string, overrides: ColumnOverrides) => {
	try {
		if (Object.keys(overrides).length === 0) {
			globalThis.sessionStorage?.removeItem(`${STORAGE_PREFIX}${storageKey}`);
			return;
		}
		globalThis.sessionStorage?.setItem(
			`${STORAGE_PREFIX}${storageKey}`,
			JSON.stringify(overrides),
		);
	} catch {
		// sessionStorage may be unavailable (private mode / quota); fail silently
	}
};

/**
 * Low-level shared storage primitive for column-chooser overrides.
 *
 * Persists a `Record<field, boolean>` of visibility overrides under
 * `column-chooser:<storageKey>` in sessionStorage and exposes setters.
 *
 * Prefer {@link useColumnSelections} for pages that work in terms of
 * `BoardColumn[]`. Use this hook directly when callers need raw access to the
 * overrides map (e.g. to filter a separate export-column list).
 */
export const useSessionStorageOverrides = (storageKey: string) => {
	const [overrides, setOverrides] = useState<ColumnOverrides>(() =>
		readOverrides(storageKey),
	);

	useEffect(() => {
		writeOverrides(storageKey, overrides);
	}, [storageKey, overrides]);

	const setOverride = useCallback((field: string, show: boolean) => {
		setOverrides((prev) => ({ ...prev, [field]: show }));
	}, []);

	const resetOverrides = useCallback(() => {
		setOverrides({});
	}, []);

	const replaceOverrides = useCallback((next: ColumnOverrides) => {
		setOverrides(next);
	}, []);

	return {
		overrides,
		setOverride,
		resetOverrides,
		replaceOverrides,
	};
};
